import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import SEO from "../components/SEO";
import { useSiteConfig } from "../utils/siteConfig";
import api from "../api/api";
import { FiLoader } from "react-icons/fi";
import { sanitizeHtml, isHtmlContent } from "../utils/sanitizeHtml";

const PolicyPage = () => {
    const location = useLocation();
    const { siteUrl, siteName } = useSiteConfig();
    const [loading, setLoading] = useState(true);
    const [privacyPolicy, setPrivacyPolicy] = useState("");
    const [returnPolicy, setReturnPolicy] = useState("");
    const [warrantyPolicy, setWarrantyPolicy] = useState("");
    const [contactEmail, setContactEmail] = useState("");

    useEffect(() => {
        const fetchPolicies = async () => {
            try {
                const [contentRes, contactRes] = await Promise.all([
                    api.get("/site-content"),
                    api.get("/contact"),
                ]);
                if (contentRes.data?.success && contentRes.data.data) {
                    setPrivacyPolicy(contentRes.data.data.privacyPolicy || "");
                    setReturnPolicy(contentRes.data.data.returnPolicy || "");
                    setWarrantyPolicy(contentRes.data.data.warrantyPolicy || "");
                }
                if (contactRes.data?.success && contactRes.data.data?.email) {
                    setContactEmail(contactRes.data.data.email);
                }
            } catch (err) {
                console.error("Failed to load policies:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchPolicies();
    }, []);

    useEffect(() => {
        if (!loading && location.hash) {
            const targetId = location.hash.replace("#", "");
            const element = document.getElementById(targetId);
            if (element) {
                setTimeout(() => {
                    element.scrollIntoView({ behavior: "smooth" });
                }, 100);
            }
        }
    }, [location.hash, loading]);

    const formatPolicyText = (text) => {
        if (!text) return "";
        return text.split("\n").map((line, i) => (
            <p key={i} className="mt-2">
                {line}
            </p>
        ));
    };

    if (loading) {
        return (
            <div className="bg-white">
                <SEO
                    title={`Privacy & Return Policy - ${siteName}`}
                    description="Read our privacy policy and return policy."
                    canonicalUrl={`${siteUrl}/policy`}
                />
                <div className="flex h-64 items-center justify-center">
                    <FiLoader className="animate-spin text-gray-400" size={22} />
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white">
            <SEO
                title={`Privacy & Return Policy - ${siteName}`}
                description="Read our privacy policy and return policy. Learn how we protect your personal information and our hassle-free return process."
                canonicalUrl={`${siteUrl}/policy`}
            />

            {/* Hero */}
            <section className="border-b border-gray-100 bg-gray-50/60">
                <div className="mx-auto max-w-full px-5 py-10 text-center lg:px-32">
                    <span className="inline-block border-2 border-blue-400 rounded-full bg-[#2F6FED]/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-[#2F6FED] hover:bg-[#2F6FED]/20 transition">
                        Our Policies
                    </span>
                    <h1 className="mt-5 text-4xl font-bold text-[#12131A] sm:text-5xl">
                        Privacy, Return & Warranty Policy
                    </h1>
                    <p className="mx-auto mt-4 max-w-5xl text-gray-500">
                        We are committed to protecting your privacy and ensuring a seamless shopping experience. <br /> Please review our policies below.
                        If you have any questions, feel free to contact us at <a href="mailto:{contactEmail}" className="text-[#2F6FED]">{contactEmail}</a>.
                    </p>
                </div>
            </section>

            <div className="mx-auto max-w-full px-5 py-16 lg:px-32">
                <div className="mx-auto max-w-full space-y-16">
                    {/* Privacy Policy */}
                    <section id="privacy-policy" className="scroll-mt-24">
                        <h2 className="text-3xl font-bold text-[#12131A]">Privacy Policy</h2>
                        <p className="mt-2 text-sm text-gray-500">Last updated: July 2026</p>

                        <div className="mt-8 text-sm text-gray-700 leading-7">
                            {privacyPolicy ? (
                                isHtmlContent(privacyPolicy) ? (
                                    <div
                                        className="prose-theme leading-relaxed"
                                        dangerouslySetInnerHTML={{ __html: sanitizeHtml(privacyPolicy) }}
                                    />
                                ) : (
                                    <div>{formatPolicyText(privacyPolicy)}</div>
                                )
                            ) : (
                                <p className="text-gray-500">Privacy policy content not available.</p>
                            )}
                        </div>
                    </section>

                    {/* Return Policy */}
                    <section id="return-policy" className="border-t border-gray-100 pt-16 scroll-mt-24">
                        <h2 className="text-3xl font-bold text-[#12131A]">Return Policy</h2>
                        <p className="mt-2 text-sm text-gray-500">Last updated: July 2026</p>

                        <div className="mt-8 text-sm text-gray-700 leading-7">
                            {returnPolicy ? (
                                isHtmlContent(returnPolicy) ? (
                                    <div
                                        className="prose-theme leading-relaxed"
                                        dangerouslySetInnerHTML={{ __html: sanitizeHtml(returnPolicy) }}
                                    />
                                ) : (
                                    <div>{formatPolicyText(returnPolicy)}</div>
                                )
                            ) : (
                                <p className="text-gray-500">Return policy content not available.</p>
                            )}
                        </div>
                    </section>

                    {/* Warranty Policy */}
                    <section id="warranty-policy" className="border-t border-gray-100 pt-16 scroll-mt-24">
                        <h2 className="text-3xl font-bold text-[#12131A]">Warranty Policy</h2>
                        <p className="mt-2 text-sm text-gray-500">Last updated: July 2026</p>

                        <div className="mt-8 text-sm text-gray-700 leading-7">
                            {warrantyPolicy ? (
                                isHtmlContent(warrantyPolicy) ? (
                                    <div
                                        className="prose-theme leading-relaxed"
                                        dangerouslySetInnerHTML={{ __html: sanitizeHtml(warrantyPolicy) }}
                                    />
                                ) : (
                                    <div>{formatPolicyText(warrantyPolicy)}</div>
                                )
                            ) : (
                                <p className="text-gray-500">Warranty policy content not available.</p>
                            )}
                        </div>
                    </section>

                    {/* Need Help */}
                    <section className="rounded-3xl border border-gray-100 bg-gray-50/60 p-8 text-center sm:p-12">
                        <h3 className="text-2xl font-bold text-[#12131A]">Need Help?</h3>
                        <p className="mx-auto mt-3 max-w-xl text-sm text-gray-600">
                            If you have questions about our policies or need assistance with a return, our customer support team is here to help.
                        </p>
                        <div className="mt-6 flex flex-wrap items-center justify-center gap-4">
                            <a
                                href="/contact"
                                className="inline-flex items-center justify-center rounded-xl bg-[#2F6FED] px-6 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-[#2F6FED]/90"
                            >
                                Contact Us
                            </a>
                            <a
                                href={contactEmail ? `mailto:${contactEmail}` : "/contact"}
                                className="inline-flex items-center justify-center rounded-xl border border-gray-200 px-6 py-3 text-sm font-semibold text-[#12131A] transition hover:border-gray-300"
                            >
                                {contactEmail || "Email Us"}
                            </a>
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default PolicyPage;
